import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Check, X, MapPin, Cpu, History, AlertTriangle, Tag, ChevronDown, ChevronUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtTime, fmtDateTime, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { TAG_LABEL, TAG_CLS } from "./tagStyles";

export function CommentEntry({ incident, onAdded, prompt }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const qc = useQueryClient();
  useEffect(() => { if (prompt && !text) setText(prompt); /* eslint-disable-next-line */ }, [prompt]);
  const submit = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/comments`, { text: t });
      setText("");
      qc.invalidateQueries({ queryKey: ["incident", incident.id] });
      onAdded?.(data);
      const nonGps = (data.tags || []).filter((x) => x.type !== "gps");
      if (nonGps.length) {
        const hits = Object.values(data.related || {}).reduce((n, v) => n + v.length, 0);
        toast.info(`${nonGps.length} entit${nonGps.length === 1 ? "y" : "ies"} tagged${hits ? ` — ${hits} prior contact${hits === 1 ? "" : "s"} surfaced` : ""}`);
      }
      if ((data.tags || []).some((x) => x.type === "gps")) toast.info('GPS coordinate detected — "Set as incident location?" offered in the timeline');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current?.focus(); }
  };
  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } };
  return (
    <div className="rounded-md border bg-background/70 focus-within:border-ring" data-testid="comment-entry">
      <textarea ref={ref} data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} rows={3}
        placeholder="Narrative / comments — descriptions, weapons, injuries, vehicles, directions of travel, callback info… Enter to add, Shift+Enter for a new line."
        className="w-full resize-y bg-transparent px-3 py-2 text-[13px] outline-none min-h-[72px]" />
      <div className="flex items-center justify-between px-2 pb-1.5">
        <span className="overline-label">Entry is timestamped and attributed to you · plates, phones, DL, DOB and GPS are tagged automatically</span>
        <button data-testid="add-comment-button" disabled={!text.trim() || busy} onClick={submit} className="h-7 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">Add Comment ⏎</button>
      </div>
    </div>
  );
}

function GpsOffer({ tag, incident, comment }) {
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const alreadySet = incident.location_lat === tag.lat && incident.location_lng === tag.lng;
  const set = async (lat, lng) => {
    setBusy(true);
    try { await api.post(`/incidents/${incident.id}/location-point`, { lat, lng, source: tag.detected_by === "manual" ? "gps_manual" : "gps_narrative", note: `from entry "${tag.display}"` }); qc.invalidateQueries({ queryKey: ["incident", incident.id] }); toast.success("Incident location set from GPS coordinate"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`gps-tag-${comment.id}`}>
      <span className={`chip mono ${TAG_CLS.gps}`}><MapPin className="h-3 w-3" /> GPS {tag.lat}, {tag.lng}</span>
      {incident.status !== "finished" && !alreadySet && (tag.ambiguous ? (
        <>
          <span className="text-amber-600 dark:text-amber-300">Order unclear — set as incident location using:</span>
          <button data-testid={`gps-set-location-${comment.id}-0`} disabled={busy} onClick={() => set(tag.lat, tag.lng)} className="h-6 px-2 rounded border mono hover:bg-accent">lat {tag.lat}, lng {tag.lng}</button>
          {tag.alternatives.map((a, i) => <button key={i} data-testid={`gps-set-location-${comment.id}-${i + 1}`} disabled={busy} onClick={() => set(a.lat, a.lng)} className="h-6 px-2 rounded border mono hover:bg-accent">lat {a.lat}, lng {a.lng}</button>)}
        </>
      ) : (
        <button data-testid={`gps-set-location-${comment.id}`} disabled={busy} onClick={() => set(tag.lat, tag.lng)} className="h-6 px-2 rounded-md border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-colors">Set as incident location?</button>
      ))}
      {alreadySet && <span className="text-teal-600 dark:text-teal-300">✓ current incident location</span>}
    </div>
  );
}

function EntityTag({ tag, matches, onOpenIncident }) {
  const [open, setOpen] = useState(false);
  const n = matches?.length || 0;
  return (
    <span className="inline-flex flex-col items-start" data-testid={`entity-tag-${tag.type}-${tag.value}`}>
      <span className="inline-flex items-center gap-1">
        <span className={`chip mono ${TAG_CLS[tag.type] || TAG_CLS.other}`} title={tag.detected_by === "manual" ? `Tagged manually by ${tag.confirmed_by}` : "Detected by pattern"}>
          <Tag className="h-3 w-3" /> {TAG_LABEL[tag.type] || tag.type} {tag.display || tag.value}{tag.detected_by === "manual" ? " ·m" : ""}
        </span>
        {n > 0 ? (
          <button data-testid={`entity-tag-matches-${tag.type}-${tag.value}`} onClick={() => setOpen((o) => !o)} className="chip border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20">
            <History className="h-3 w-3" /> {n} prior contact{n === 1 ? "" : "s"} {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        ) : <span className="text-[10px] text-muted-foreground">no prior contacts</span>}
      </span>
      {open && (
        <span className="mt-0.5 ml-1 flex flex-col gap-0.5" data-testid={`entity-tag-matches-list-${tag.type}-${tag.value}`}>
          {matches.map((m) => (
            <button key={m.id} onClick={() => onOpenIncident(m.id)} className="text-left text-[11px] hover:underline">
              <span className="mono text-primary">{m.incident_number}</span> · {fmtDate(m.received_at)} · {m.call_type_name} · {m.location_raw} · {m.disposition_label || m.status.toUpperCase()}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

const MANUAL_TYPES = [["name", "Name"], ["plate", "Plate"], ["phone", "Phone"], ["dl", "DL #"], ["dob", "DOB"], ["gps", "GPS"], ["other", "Other"]];

function ManualTagBar({ comment, selection, onDone }) {
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const tag = async (type) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/comments/${comment.id}/tags`, { type, value: selection, display: selection });
      qc.invalidateQueries({ queryKey: ["incident", comment.incident_id] });
      toast.success(`Tagged "${selection}" as ${TAG_LABEL[type]}${data.related?.length ? ` — ${data.related.length} prior contact(s)` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 rounded-md border bg-popover px-2 py-1 text-[11px]" data-testid={`manual-tag-bar-${comment.id}`}>
      <span className="text-muted-foreground">Tag "<span className="mono">{selection}</span>" as:</span>
      {MANUAL_TYPES.map(([t, l]) => <button key={t} data-testid={`manual-tag-${t}`} disabled={busy} onClick={() => tag(t)} className={`chip ${TAG_CLS[t] || TAG_CLS.other} hover:brightness-110`}>{l}</button>)}
      <button data-testid="manual-tag-cancel" onClick={onDone} className="ml-auto text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
    </div>
  );
}

function CommentItem({ c, incident, canEdit, tagMatches, onOpenIncident }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.text);
  const [showHistory, setShowHistory] = useState(false);
  const [selection, setSelection] = useState("");
  const qc = useQueryClient();
  const system = c.kind !== "user";
  const alertImport = c.kind === "alert_import";
  const save = async () => {
    try { await api.patch(`/incidents/comments/${c.id}`, { text }); setEditing(false); qc.invalidateQueries({ queryKey: ["incident", incident.id] }); toast.success("Comment corrected — original retained in audit history"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onMouseUp = () => {
    if (system || incident.status === "finished" || editing) return;
    const sel = window.getSelection();
    const s = sel ? sel.toString().trim() : "";
    if (s.length >= 2 && s.length <= 80 && c.text.includes(s)) setSelection(s);
  };
  return (
    <div data-testid={`comment-${c.id}`} className={`px-2.5 py-1.5 border-b border-border/50 ${alertImport ? "bg-amber-500/10 border-l-4 border-l-amber-500" : system ? "bg-muted/30" : ""}`}>
      <div className="flex items-start gap-2">
        <span className="mono text-[11px] text-muted-foreground tabular-nums shrink-0 mt-0.5" title={fmtDateTime(c.created_at)}>{fmtTime(c.created_at)}</span>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex gap-1.5">
              <textarea data-testid={`comment-edit-input-${c.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2} className="field-input !h-auto py-1 flex-1" autoFocus />
              <button data-testid={`comment-edit-save-${c.id}`} onClick={save} className="h-7 w-7 rounded border hover:bg-accent flex items-center justify-center"><Check className="h-3.5 w-3.5" /></button>
              <button data-testid={`comment-edit-cancel-${c.id}`} onClick={() => { setEditing(false); setText(c.text); }} className="h-7 w-7 rounded border hover:bg-accent flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <div onMouseUp={onMouseUp} className={`text-[13px] whitespace-pre-wrap break-words ${alertImport ? "text-amber-800 dark:text-amber-100" : system ? "mono text-xs text-muted-foreground" : ""}`} data-testid={`comment-text-${c.id}`}>
              {alertImport ? <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-amber-500" /> : system ? <Cpu className="inline h-3 w-3 mr-1 -mt-0.5 text-teal-500" /> : null}{c.text}
            </div>
          )}
          {selection && !editing && <ManualTagBar comment={c} selection={selection} onDone={() => setSelection("")} />}
          {!system && c.tags?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5 items-start" data-testid={`comment-tags-${c.id}`}>
              {c.tags.map((t, i) => t.type === "gps"
                ? <GpsOffer key={i} tag={t} incident={incident} comment={c} />
                : <EntityTag key={i} tag={t} matches={tagMatches?.[`${t.type}:${t.value}`]} onOpenIncident={onOpenIncident} />)}
            </div>
          )}
          <div className="flex items-center gap-2 mt-0.5 overline-label !text-[9px]">
            <span>{alertImport ? "SYSTEM · AUTO-IMPORTED ALERT (not dispatcher-typed)" : system ? "SYSTEM" : c.author_name}</span>
            {c.edited && <button data-testid={`comment-history-toggle-${c.id}`} onClick={() => setShowHistory((s) => !s)} className="text-amber-600 dark:text-amber-300 hover:underline flex items-center gap-0.5"><History className="h-3 w-3" /> edited ({c.edits.length})</button>}
            {!system && canEdit && !editing && incident.status !== "finished" && (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-muted-foreground/70">select text to tag</span>
                <button data-testid={`comment-edit-button-${c.id}`} onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground flex items-center gap-0.5"><Pencil className="h-3 w-3" /> correct typo</button>
              </span>
            )}
          </div>
          {showHistory && (
            <div className="mt-1 rounded border bg-background/60 p-2 space-y-1" data-testid={`comment-history-${c.id}`}>
              {c.edits.map((e, i) => (
                <div key={i} className="text-[11px]"><span className="mono text-muted-foreground">{fmtDateTime(e.edited_at)}</span> · {e.editor_name}: <span className="line-through text-muted-foreground">{e.previous_text}</span> → {e.new_text}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommentsTimeline({ incident, onOpenIncident }) {
  const { user } = useAuth();
  return (
    <div className="flex-1 min-h-0 overflow-auto" data-testid="comments-timeline">
      {incident.comments.length === 0 && <div className="p-3 text-xs text-muted-foreground">No entries yet.</div>}
      {incident.comments.map((c) => <CommentItem key={c.id} c={c} incident={incident} canEdit={!!user} tagMatches={incident.tag_matches} onOpenIncident={onOpenIncident} />)}
    </div>
  );
}
